import React, { useEffect, useState, useRef } from "react";
import renderAssistantMessage from "../utils/renderAssistantMessage";
import Icon from "../../YourAIAgents/components/components/icon";
import { CircularProgress } from "@mui/material";
import { useUsersContext } from "../../../context/usersContext";
import { useUserInfo } from "../../../context/user";
import { useNavigate, useParams } from "react-router-dom";
import { Add, AddCommentOutlined, InfoOutlined } from "@mui/icons-material";
import PermissionRequiredModal from "./PermissionRequiredModal";
import { findAutomatedTask } from "../utils/taskSchedule";
import UserMessage from "./ConversationSections/UserMessage";
import AIMessage from "./ConversationSections/AIMessage";
import RotatingWaitMessage from "./RotatingWaitMessage";

const Conversation = ({
  agencyInfo,
  submitNewMessage,
  UploadOnKB,
  IsStreamApi,
  selectedFiles,
}) => {
  const params = useParams();
  const {
    fetchMessages,
    skipNextFetchIdRef,
    resumePendingChat,
    messages,
    isUserChating,
    agentPlaceholderText,
    setOpenShareModal,
    chatError,
    setChatError,
    tasksData,
  } = useUsersContext();
  const scheduledTasks = tasksData?.prompt || [];
  const [userInfo] = useUserInfo();
  const [loading, setLoading] = useState(false);
  const [openPermissionModal, setOpenPermissionModal] = useState(false);
  const navigate = useNavigate();

  const handleErrorButtonClick = () => {
    if (
      chatError?.showError &&
      chatError?.errorMessage?.includes("couldn't find this conversation")
    ) {
      navigate("/app/chat/");
      setChatError({
        showError: false,
        errorMessage: "",
        classNames: "",
        iconColor: "",
      });
    } else if (
      chatError?.showError &&
      chatError?.errorMessage?.includes("agent set up for this yet")
    ) {
      if (userInfo?.role !== "user") {
        navigate("/app/your-ai-agents/");
        setChatError({
          showError: false,
          errorMessage: "",
          classNames: "",
          iconColor: "",
        });
      } else {
        setOpenPermissionModal(true);
      }
    }
  };

  const bottomRef = useRef(null);
  const [copied, setCopied] = useState(null);
  const [speakingMessageId, setSpeakingMessageId] = useState(null);

  const getMessages = async () => {
    // Just-created conversation: messages are already in state from the
    // optimistic send, and fetchMessages will skip its network call for
    // this id — don't flash the full-page spinner over them.
    const usingCachedMessages = skipNextFetchIdRef?.current === params.id;
    if (!usingCachedMessages) setLoading(true);
    const data = await fetchMessages(params.id);
    setLoading(false);
    resumePendingChat?.(params.id, data);
  };

  useEffect(() => {
    if (params.id) getMessages();
  }, [params.id]);

  const copyToClipboard = (content, id) => {
    const processedContent = (content || "")
      .replace(/\\n/g, "\n")
      .replace(/\\t/g, "\t")
      .replace(/\\r/g, "\r")
      .replace(/\\\\/g, "\\")
      .replace(/\\"/g, '"');
    const htmlContent = renderAssistantMessage(processedContent);

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-9999px";
    document.body.appendChild(tempDiv);

    const range = document.createRange();
    range.selectNodeContents(tempDiv);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    try {
      document.execCommand("copy");
      setCopied(id);
    } catch (err) {
      console.error("Failed to copy content: ", err);
    }

    document.body.removeChild(tempDiv);
  };

  const handleOpenPromptModal = (content) => {
    navigate("/app/chat/tasks/new", {
      state: { autoloadfirsttext: content || "" },
    });
  };

  const handleViewSchedule = (taskId) => {
    if (!taskId) return;
    navigate(`/app/chat/tasks/${taskId}/edit`);
  };

  const handleCreateTable = () =>
    submitNewMessage(
      "Convert this response to the table format",
      "notsavetodb",
      "notimage",
      () => {},
      false,
    );

  // Same "image" message type the composer sends when the image agent is
  // active, so the reply comes back rendered as an image instead of text.
  const handleFormatImage = () =>
    submitNewMessage(
      "Convert this response into an image",
      "notsavetodb",
      "image",
      () => {},
      false,
    );

  const handleTryAgain = () =>
    submitNewMessage(
      "Rewrite the response",
      "notsavetodb",
      "notimage",
      () => {},
      false,
    );

  const handleContinue = () =>
    submitNewMessage(
      "Continue",
      "notsavetodb",
      "notimage",
      () => {},
      false,
    );

  const textToSpeak = (text, messageId) => {
    if (!text?.trim()) return;
    if (speechSynthesis.speaking) speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.onend = () => setSpeakingMessageId(null);
    speechSynthesis.speak(utterance);
    setSpeakingMessageId(messageId);
  };

  const stopSpeech = () => {
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
      setSpeakingMessageId(null);
    }
  };

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ block: "end" });
    }
  }, [messages]);

  if (loading)
    return (
      <div className="w-full h-full flex flex-col gap-3 items-center justify-center">
        <CircularProgress size={40} />
        <p className="text-[12px]">Loading Messages, please wait...</p>
      </div>
    );

  return (
    <div className="conversation__wrapper mx-auto w-full relative overflow-y-auto overflow-x-hidden v3chat__content px-6">
      <div
        className="flex items-center gap-2 absolute top-1 left-0 w-fit cursor-pointer py-2"
        onClick={() => setOpenShareModal(params.id)}
      >
        <Icon id="share" style={{ fontSize: "12px" }} />
        <p className="text-foreground font-[400] text-[12px]">Share</p>
      </div>

      <div className="text-center relative mt-2.5 mb-6">
        <span className="bg-muted inline-block text-foreground text-[0.75rem] py-1.5 px-2.5 rounded-[5px]">
          {`Welcome ${userInfo?.userName || ""}`}
        </span>
      </div>

      <div className="flex flex-col w-full">
        {messages?.map((message, msgIndex) => {
          const processedContent = (message.content || "")
            .replace(/\\n/g, "\n")
            .replace(/\\t/g, "\t")
            .replace(/\\r/g, "\r")
            .replace(/\\\\/g, "\\")
            .replace(/\\"/g, '"');
          const htmlContent = renderAssistantMessage(processedContent);

          if (message.sender) {
            return (
              <AIMessage
                key={msgIndex}
                message={message}
                msgIndex={msgIndex}
                htmlContent={htmlContent}
                conversation_id={params?.id}
                isLatestMessage={
                  msgIndex === messages.length - 1 &&
                  message.status !== "read"
                }
                IsStreamApi={IsStreamApi}
                agencyInfo={agencyInfo}
                copied={copied}
                onCopy={copyToClipboard}
                onSaveAutomate={handleOpenPromptModal}
                onCreateTable={handleCreateTable}
                onFormatImage={handleFormatImage}
                onTryAgain={handleTryAgain}
                onContinue={handleContinue}
                onSpeak={textToSpeak}
                onStopSpeak={stopSpeech}
                isSpeaking={speakingMessageId === msgIndex}
              />
            );
          }

          return (
            <UserMessage
              key={msgIndex}
              message={message}
              msgIndex={msgIndex}
              onOpenPromptModal={handleOpenPromptModal}
              onViewSchedule={handleViewSchedule}
              automatedTask={findAutomatedTask(message.content, scheduledTasks)}
              onUploadKB={UploadOnKB}
            />
          );
        })}

        <div className="relative mt-[20px] ml-2">
          {isUserChating &&
            (selectedFiles?.length > 0 ? (
              <div className="flex items-center gap-1 text-muted-foreground text-[14px]">
                <Icon
                  id="aichatIconInput"
                  style={{ marginRight: "5px", fontSize: "12px" }}
                />
                Analysing files
              </div>
            ) : (
              <RotatingWaitMessage fallbackText={agentPlaceholderText} />
            ))}
        </div>

        <div id="errorofmaxfile" />

        {chatError?.showError && chatError?.errorMessage !== "" && (
          <div
            className={`w-[97%] rounded-[25px] m-auto mt-4 ${
              chatError?.classNames || "border !border-destructive bg-destructive/10"
            }`}
          >
            <div className="flex items-center justify-between w-full px-[30px] py-[22px]">
              <p className="py-2.5 px-0 m-0 text-[14px] flex text-foreground">
                <span className="pr-4">
                  <InfoOutlined
                    sx={{ color: chatError?.iconColor || "hsl(var(--destructive))" }}
                  />
                </span>
                <span>{chatError?.errorMessage}</span>
              </p>
              {(chatError?.errorMessage?.includes(
                "couldn't find this conversation",
              ) ||
                chatError?.errorMessage?.includes(
                  "agent set up for this yet",
                )) && (
                <button
                  onClick={handleErrorButtonClick}
                  className="px-[22px] py-2.5 text-[14px] bg-card border border-border rounded-[5px]"
                >
                  <span className="pr-3">
                    {chatError?.errorMessage?.includes(
                      "agent set up for this yet",
                    ) ? (
                      <Add />
                    ) : (
                      <AddCommentOutlined
                        sx={{ transform: "rotateY(180deg)", color: "hsl(var(--foreground))" }}
                      />
                    )}
                  </span>
                  <span>
                    {chatError?.errorMessage?.includes(
                      "agent set up for this yet",
                    )
                      ? "Build a custom Agent"
                      : "Start New Chat"}
                  </span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {openPermissionModal && (
        <PermissionRequiredModal
          isOpen={openPermissionModal}
          onClose={() => setOpenPermissionModal(false)}
        />
      )}

      {/* scroll-mb keeps the last message clear of the sticky composer
          (card + icon row + footer stack ≈ 285px) */}
      <div ref={bottomRef} id="gotovie" className="scroll-mb-80" />
    </div>
  );
};

export default Conversation;
